/**
 * Pure helpers for Chronos screens, extracted from components so they
 * are testable without mounting UI. Stateless, no DB access.
 */
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

// ─── Time and formatting ─────────────────────────────────────────────────────

const PAD_WIDTH = 2;
const MS_IN_SEC = 1000;
const SECS_IN_MIN = 60;
const MINS_IN_HOUR = 60;
const HOURS_IN_DAY = 24;
const MS_IN_CENTISECOND = 10;
const SECONDS_IN_HOUR = 3600;

export const padTwo = (n: number) => n.toString().padStart(PAD_WIDTH, "0");

/** ISO → value for `<input type="datetime-local">` in local time. */
export function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): date format, not UI text
  return `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}T${padTwo(d.getHours())}:${padTwo(d.getMinutes())}`;
}

/** Value of `<input type="datetime-local">` → ISO UTC, or null if invalid. */
export function fromLocalInputValue(value: string): string | null {
  if (!value) {
    return null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Timer display parts (HH:MM:SS + centiseconds). */
export function formatTimeParts(totalMs: number) {
  const totalSeconds = Math.floor(totalMs / MS_IN_SEC);
  const hours = Math.floor(totalSeconds / SECONDS_IN_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECS_IN_MIN);
  const seconds = totalSeconds % SECS_IN_MIN;
  const centiseconds = Math.floor((totalMs % MS_IN_SEC) / MS_IN_CENTISECOND);

  const mainPart = [padTwo(hours), padTwo(minutes), padTwo(seconds)].join(":");
  return { mainPart, msPart: padTwo(centiseconds) };
}

/** Timestamp relativo del feed ("Just now", "5m ago", "3h ago", fecha). */
export function formatTimestamp(isoString: string | null, now: number = Date.now()): string {
  if (!isoString) {
    return "";
  }
  try {
    const diffMs = now - new Date(isoString).getTime();
    const diffMins = Math.floor(diffMs / (SECS_IN_MIN * MS_IN_SEC));
    if (diffMins < 1) {
      return i18n._(msg`Just now`);
    }
    if (diffMins < MINS_IN_HOUR) {
      return i18n._(msg`${diffMins}m ago`);
    }
    const diffHours = Math.floor(diffMins / MINS_IN_HOUR);
    if (diffHours < HOURS_IN_DAY) {
      return i18n._(msg`${diffHours}h ago`);
    }
    return new Date(isoString).toLocaleDateString();
  } catch {
    return "";
  }
}

/** Minutos → "2h 5m" / "45m". */
export function formatMinsStr(mins: number): string {
  const h = Math.floor(mins / MINS_IN_HOUR);
  const m = mins % MINS_IN_HOUR;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── Team presence and feed ─────────────────────────────────────────────────

export type PresenceStatus = "online" | "focus" | "away" | "offline";

export function castStatus(statusStr: string | null): PresenceStatus {
  if (
    statusStr === "online" ||
    statusStr === "focus" ||
    statusStr === "away" ||
    statusStr === "offline"
  ) {
    return statusStr;
  }
  return "offline";
}

interface FeedEventContentInput {
  event_type: string | null;
  event_data: string | null;
}

interface FeedEventData {
  description?: string;
  duration?: number;
  message?: string;
}

const DEFAULT_FOCUS_MINUTES = 25;

function isFeedEventData(obj: unknown): obj is FeedEventData {
  return typeof obj === "object" && obj !== null;
}

/** Readable text for a feed event based on its type. */
export function getFeedEventContent(evt: FeedEventContentInput): string {
  try {
    const raw = evt.event_data ? (JSON.parse(evt.event_data) as unknown) : null;
    if (isFeedEventData(raw)) {
      const what = raw.description ?? i18n._(msg`Deep Work`);
      const mins = raw.duration ?? DEFAULT_FOCUS_MINUTES;
      const message = raw.message ?? "";
      if (evt.event_type === "focus_started") {
        return i18n._(msg`started a Focus session: "${what}"`);
      }
      if (evt.event_type === "focus_completed") {
        return i18n._(msg`completed a ${mins}-minute Focus cycle: "${what}"`);
      }
      if (evt.event_type === "status_update") {
        return i18n._(msg`shared a status update: "${message}"`);
      }
      return message || (evt.event_data ?? "");
    }
    return evt.event_data ?? "";
  } catch {
    return evt.event_data ?? "";
  }
}

// ─── Proyectos: estados y colores ───────────────────────────────────────────

export function getStatusLabel(status: string): string {
  switch (status.toLowerCase()) {
    case "active": {
      return i18n._(msg`Active`);
    }
    case "paused": {
      return i18n._(msg`Paused`);
    }
    case "review": {
      return i18n._(msg`Review`);
    }
    case "completed": {
      return i18n._(msg`Completed`);
    }
    default: {
      return status;
    }
  }
}

export function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "active": {
      return "bg-primary/10 text-primary border border-primary/20";
    }
    case "paused": {
      return "bg-amber-500/10 text-amber-400 border border-amber-500/20";
    }
    case "review": {
      return "bg-purple-500/10 text-purple-400 border border-purple-500/20";
    }
    case "completed": {
      return "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
    }
    default: {
      return "bg-white/5 text-muted-foreground border border-white/5";
    }
  }
}

// Static map: Tailwind only generates classes that appear literally in the
// source code, so never build `text-${base}` at runtime.
export const COLOR_STYLES: Record<string, { text: string; bar: string }> = {
  "bg-blue-500": { text: "text-blue-400", bar: "bg-blue-500" },
  "bg-purple-500": { text: "text-purple-400", bar: "bg-purple-500" },
  "bg-emerald-500": { text: "text-emerald-400", bar: "bg-emerald-500" },
  "bg-amber-500": { text: "text-amber-400", bar: "bg-amber-500" },
};
export const DEFAULT_COLOR_STYLE = { text: "text-blue-400", bar: "bg-blue-500" };

export const getColorClass = (color: string) => COLOR_STYLES[color] ?? DEFAULT_COLOR_STYLE;

// ─── Analytics: heuristic categorization ────────────────────────────────────

type EntryCategory = "design" | "admin" | "coding";

export function getCategoryForEntry(desc: string, projCode: string): EntryCategory {
  const normalized = desc.toLowerCase();
  if (
    projCode === "MKT" ||
    normalized.includes("design") ||
    normalized.includes("figma") ||
    normalized.includes("ui") ||
    normalized.includes("ux") ||
    normalized.includes("layout")
  ) {
    return "design";
  }
  if (
    projCode === "ADM" ||
    normalized.includes("admin") ||
    normalized.includes("meeting") ||
    normalized.includes("email") ||
    normalized.includes("doc")
  ) {
    return "admin";
  }
  return "coding";
}

// ─── Workspaces ─────────────────────────────────────────────────────────────

/** URL-safe slug derived from workspace name. */
export function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-|-$/g, "");
}
